import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    OnInit,
    signal,
    viewChild,
    WritableSignal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ColorSketchModule } from 'ngx-color/sketch';
import { firstValueFrom } from 'rxjs';

import { LayerBloc } from '@connect-services/client/blocs/layer.bloc';
import { TitleComponent } from '@connect-services/client/components/field-intervention-title/title/title.component';
import { WIDTH_PX_BREAKPOINT } from '@connect-services/client/components/project-settings/edit-layer-template/edit-layer-template.component';
import { SymbologyGeometryFormComponent } from '@connect-services/client/components/project-settings/symbology-edit/symbology-geometry-form/symbology-geometry-form.component';
import {
    FeatureCollection,
    Layer,
    LayersService,
    MapboxStyle,
    PermissionAliasEnum,
} from '@connect-services/client/sdk/api';
import { AssertService } from '@connect-services/client/services/assert.service';
import { ColorConverterService } from '@connect-services/client/services/color-converter.service';
import { DetailPanelService } from '@connect-services/client/services/detail-panel.service';
import { labelByLayerType } from '@connect-services/client/services/layer.service';
import { LayerEditService } from '@connect-services/client/services/layer-edit.service';
import {
    DEFAULT_SYMBOL,
    DEFAULT_SYMBOL_SIZE,
    NO_SYMBOL,
    SymbologyEditService,
} from '@connect-services/client/services/symbology-edit.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { FormService } from '@shared/client/services/form.service';

export interface LayerSymbologyFormType {
    line: FormGroup<GeometryFormGroup>;
    point: FormGroup<GeometryFormGroup>;
    polygon: FormGroup<GeometryFormGroup>;
}

export interface GeometryFormGroup {
    color: FormControl<string>;
    label: FormGroup<LabelFormType>;
    maxZoom: FormControl<number | null>;
    minZoom: FormControl<number | null>;
    opacity: FormControl<number | null>;
    size?: FormControl<number | null>;
    symbol: FormControl<string>;
    symbolOpacity?: FormControl<number | null>;
}

export interface LabelFormType {
    label: FormControl<string | null>;
    maxZoom: FormControl<number | null>;
    minZoom: FormControl<number | null>;
    size: FormControl<number | null>;
}

type MergedGeometryStyle = {
    layout: { [key: string]: unknown };
    maxZoom: number | null;
    minZoom: number | null;
    paint: { [key: string]: unknown };
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        ColorSketchModule,
        LoadingComponent,
        ReactiveFormsModule,
        RessourcesModule,
        SymbologyGeometryFormComponent,
        TitleComponent,
    ],
    selector: 'app-symbology-edit',
    styleUrls: [
        'symbology-edit.component.scss',
        '../project-settings-layer-detail-template/project-settings-layer-detail-template.component.scss',
        '../edit-deposit-layer/edit-deposit-layer.component.scss',
    ],
    templateUrl: './symbology-edit.component.html',
})
export class SymbologyEditComponent implements OnInit {
    protected readonly businessIdentifier: WritableSignal<string | null> = signal(null);
    protected readonly ButtonType = ButtonType;
    protected readonly detailDiv = viewChild.required<ElementRef<HTMLDivElement>>('detailDiv');
    protected readonly formService = inject(FormService);
    protected readonly formStyleLoaded = signal(false);
    protected readonly isWide = computed(
        () =>
            this.detailPanelService.expandedVertically() &&
            this.detailDiv()?.nativeElement.offsetWidth > WIDTH_PX_BREAKPOINT,
    );
    protected readonly labelByLayerType = labelByLayerType;
    protected readonly Layer = Layer;
    protected readonly layerBloc = inject(LayerBloc);
    protected readonly MapboxStyle = MapboxStyle;
    protected readonly PermissionAliasEnum = PermissionAliasEnum;
    protected readonly symbologyEditService = inject(SymbologyEditService);
    protected readonly title = computed(() => this.layerBloc.value()?.name ?? '');

    private readonly destroyRef = inject(DestroyRef);
    private readonly detailPanelService = inject(DetailPanelService);
    private readonly layerEditService = inject(LayerEditService);
    private readonly layersService = inject(LayersService);
    private readonly toolsService = inject(ColorConverterService);

    public constructor() {
        this.destroyRef.onDestroy(() => {
            this.symbologyEditService.resetForm();
        });
    }

    public async ngOnInit(): Promise<void> {
        const layer = this.layerBloc.value();
        if (!layer) {
            return;
        }

        this.businessIdentifier.set(layer.businessIdentifier);

        await this.symbologyEditService.loadStyle();

        this.patchFormGroupValues();
        await this.symbologyEditService.setBookmarkedAttributes();

        this.symbologyEditService.setSourcesVisibility();
        const geoJSON = await this.fetchGeoJsonEntities(layer);
        this.symbologyEditService.entitiesGeoJSON.set(geoJSON);

        this.formStyleLoaded.set(true);
    }

    protected closeSymbology(): void {
        this.layerEditService.isEditingSymbology.set(false);
    }

    protected patchFormGroupValues(): void {
        const lineForm = this.symbologyEditService.hasLine()
            ? this.initLineOrFillFormGroup(
                  MapboxStyle.TypeEnum.LINE,
                  this.symbologyEditService.isLineFormVisible,
                  this.symbologyEditService.lineColor,
              )
            : this.symbologyEditService.cleanLineFormGroup;
        const polygonForm = this.symbologyEditService.hasPolygon()
            ? this.initLineOrFillFormGroup(
                  MapboxStyle.TypeEnum.FILL,
                  this.symbologyEditService.isPolygonFormVisible,
                  this.symbologyEditService.polygonColor,
              )
            : this.symbologyEditService.cleanPolygonFormGroup;
        const pointForm = this.symbologyEditService.hasSymbol()
            ? this.initPointFormGroup()
            : this.symbologyEditService.cleanPointFormGroup;

        this.symbologyEditService.formGroup.reset({
            line: lineForm.value,
            point: pointForm.value,
            polygon: polygonForm.value,
        });
    }

    private fetchGeoJsonEntities(layer: Layer): Promise<FeatureCollection | undefined> {
        try {
            return firstValueFrom(
                this.layersService.layerControllerGetEntitiesGeoJson({ excludeProperties: false, id: layer.id }),
            );
        } catch {
            return new Promise((resolve) => resolve(undefined));
        }
    }

    private getHexSymbolColor(color: string): string {
        const rgbValues: { blue: number; green: number; red: number } | null = this.toolsService.parseRGB(color);
        if (!rgbValues) {
            return '';
        }

        return this.toolsService.rgbToHex(rgbValues!.red, rgbValues!.green, rgbValues!.blue);
    }

    private getLabelFormGroupForGeometry(geometry: MapboxStyle.TypeEnum): FormGroup<LabelFormType> {
        const labelStyle = this.symbologyEditService
            .layerStyle()!
            .style.find(
                (style: MapboxStyle) => style.type === MapboxStyle.TypeEnum.SYMBOL && style.id === `label-${geometry}`,
            );
        let label: string | null = null;
        let labelSize: number | null = null;
        let labelMinZoom: number | null = null;
        let labelMaxZoom: number | null = null;

        if (labelStyle) {
            const labelValues = this.getLabelParameters(labelStyle);
            label = labelValues.label;
            labelSize = labelValues.labelSize;
            labelMinZoom = labelValues.minZoom;
            labelMaxZoom = labelValues.maxZoom;
        }

        return new FormGroup<LabelFormType>({
            label: new FormControl<string | null>(label),
            maxZoom: new FormControl<number | null>(labelMaxZoom),
            minZoom: new FormControl<number | null>(labelMinZoom),
            size: new FormControl<number | null>(labelSize),
        });
    }

    private getLabelParameters(labelStyle: MapboxStyle): {
        label: string | null;
        labelSize: number | null;
        maxZoom: number | null;
        minZoom: number | null;
    } {
        // need to force the Record<string, unknown>
        const layoutObject = (labelStyle?.layout ?? undefined) as Record<string, unknown> | undefined;

        let label: string | null = null;
        if (layoutObject?.['text-field']) {
            AssertService.isArray<string>(layoutObject['text-field']);
            label = layoutObject['text-field'].at(-1) ?? null;
        }

        let labelSize: number | null = null;
        const textSize = layoutObject?.['text-size'];
        if (typeof textSize === 'number') {
            AssertService.isNumber(textSize);
            labelSize = textSize;
        } else if (textSize) {
            AssertService.isArray<string | number | [string]>(textSize);
            const sizeValue = textSize.at(-1) ?? 0;
            AssertService.isNumber(sizeValue);
            labelSize = sizeValue;
        }

        const minZoom = labelStyle?.minzoom ?? null;
        if (minZoom !== null) {
            AssertService.isNumber(minZoom);
        }

        const maxZoom = labelStyle?.maxzoom ?? null;
        if (maxZoom !== null) {
            AssertService.isNumber(maxZoom);
        }

        return { label, labelSize, maxZoom, minZoom };
    }

    private getMergedStyleByGeometry(type: MapboxStyle.TypeEnum): MergedGeometryStyle | null {
        const styles: MapboxStyle[] = this.symbologyEditService
            .layerStyle()!
            .style.filter((style: MapboxStyle) => style.type === type);

        if (!styles.length) {
            return null;
        }

        return this.mergeStyles(styles);
    }

    private getStyleValuesForFill(style: MergedGeometryStyle): {
        opacity: number | null;
        symbol: string;
        symbolOpacity: number | null;
    } {
        let symbol = NO_SYMBOL;
        let symbolOpacity: number | null = null;
        let opacity: number | null = null;

        if ('fill-pattern' in style.paint) {
            AssertService.isString(style.paint['fill-pattern']);
            symbol = style.paint['fill-pattern'];
        }
        if ('fill-color' in style.paint) {
            AssertService.isString(style.paint['fill-color']);
            const hex = this.getHexSymbolColor(style.paint['fill-color']);
            this.symbologyEditService.polygonColor.set(hex);
        }
        if ('fill-opacity' in style.paint) {
            AssertService.isNumber(style.paint['fill-opacity']);
            opacity = style.paint['fill-opacity'];
        }
        if ('fill-opacity-symbol' in style.paint) {
            AssertService.isNumber(style.paint['fill-opacity-symbol']);
            symbolOpacity = style.paint['fill-opacity-symbol'];
        }

        return { opacity, symbol, symbolOpacity };
    }

    private getStyleValuesForLine(style: MergedGeometryStyle): {
        opacity: number | null;
        size: number;
        symbol: string;
    } {
        let symbol = NO_SYMBOL;
        let size = 0;
        let opacity: number | null = null;

        if ('line-pattern' in style.paint) {
            AssertService.isString(style.paint['line-pattern']);
            symbol = style.paint['line-pattern'];
        }
        if ('line-color' in style!.paint) {
            AssertService.isString(style.paint['line-color']);
            const hex = this.getHexSymbolColor(style.paint['line-color']);
            this.symbologyEditService.lineColor.set(hex);
        }
        if ('line-width' in style.paint) {
            if (typeof style.paint['line-width'] === 'number') {
                AssertService.isNumber(style.paint['line-width']);
                size = style.paint['line-width'];
            } else {
                AssertService.isArray<string | number | [string]>(style.paint['line-width']);
                const textSizeParams = style.paint['line-width'];
                const sizeValue: string | number | [string] | null = textSizeParams.at(-1) ?? null;

                AssertService.isNumber(sizeValue);
                size = sizeValue;
            }
        }
        if ('line-opacity' in style!.paint) {
            AssertService.isNumber(style.paint['line-opacity']);
            opacity = style.paint['line-opacity'];
        }

        return { opacity, size, symbol };
    }

    private initLineOrFillFormGroup(
        geometry: MapboxStyle.TypeEnum,
        isVisible: WritableSignal<boolean>,
        color: WritableSignal<string>,
    ): FormGroup<GeometryFormGroup> {
        isVisible.set(true);
        const mergedStyle = this.getMergedStyleByGeometry(geometry);
        if (!mergedStyle) {
            return geometry === MapboxStyle.TypeEnum.LINE
                ? this.symbologyEditService.cleanLineFormGroup
                : this.symbologyEditService.cleanPolygonFormGroup;
        }

        let minZoom: number | null = null;
        let maxZoom: number | null = null;
        let styleValues: {
            opacity: number | null;
            size?: number;
            symbol: string;
            symbolOpacity?: number | null;
        } = {
            opacity: null,
            symbol: NO_SYMBOL,
        };

        if (mergedStyle?.paint) {
            if (geometry === MapboxStyle.TypeEnum.FILL) {
                styleValues = this.getStyleValuesForFill(mergedStyle);
            } else if (geometry === MapboxStyle.TypeEnum.LINE) {
                styleValues = this.getStyleValuesForLine(mergedStyle);
            }
        }

        if (mergedStyle?.minZoom) {
            AssertService.isNumber(mergedStyle.minZoom);
            minZoom = mergedStyle.minZoom;
        }
        if (mergedStyle?.maxZoom) {
            AssertService.isNumber(mergedStyle.maxZoom);
            maxZoom = mergedStyle.maxZoom;
        }

        let sizeOrSymbolOpacityFormControl = {};
        if (styleValues?.size !== undefined) {
            sizeOrSymbolOpacityFormControl = {
                size: new FormControl<number>(styleValues.size),
            };
        } else if (styleValues?.symbolOpacity !== undefined) {
            sizeOrSymbolOpacityFormControl = {
                symbolOpacity: new FormControl<number | null>(styleValues.symbolOpacity),
            };
        }

        return new FormGroup<GeometryFormGroup>({
            color: new FormControl<string>(color(), { nonNullable: true }),
            label: this.getLabelFormGroupForGeometry(geometry),
            maxZoom: new FormControl<number | null>(maxZoom),
            minZoom: new FormControl<number | null>(minZoom),
            opacity: new FormControl<number | null>(styleValues.opacity),
            symbol: new FormControl<string>(styleValues.symbol, { nonNullable: true }),
            ...sizeOrSymbolOpacityFormControl,
        });
    }

    private initPointFormGroup(): FormGroup<GeometryFormGroup> {
        this.symbologyEditService.isPointFormVisible.set(true);
        const symbolStyle = this.symbologyEditService
            .layerStyle()!
            .style.find(
                (style: MapboxStyle) => style.type === MapboxStyle.TypeEnum.SYMBOL && !style.id.startsWith('label'),
            );

        let symbol = DEFAULT_SYMBOL;
        let size = DEFAULT_SYMBOL_SIZE;
        let opacity: number | null = null;
        let minZoom: number | null = null;
        let maxZoom: number | null = null;
        if (symbolStyle?.paint) {
            if ('icon-color' in symbolStyle!.paint) {
                AssertService.isString(symbolStyle.paint['icon-color']);
                const hex = this.getHexSymbolColor(symbolStyle.paint['icon-color']);
                this.symbologyEditService.pointColor.set(hex);
            }

            if ('icon-opacity' in symbolStyle!.paint) {
                AssertService.isNumber(symbolStyle.paint['icon-opacity']);
                opacity = symbolStyle.paint['icon-opacity'];
            }
        }

        if (symbolStyle?.layout) {
            if ('icon-image' in symbolStyle.layout) {
                AssertService.isString(symbolStyle.layout['icon-image']);
                symbol = symbolStyle.layout['icon-image'];
            }
            if ('icon-size' in symbolStyle.layout) {
                AssertService.isNumber(symbolStyle.layout['icon-size']);
                size = symbolStyle.layout['icon-size'];
            }
        }

        if (symbolStyle?.minzoom) {
            AssertService.isNumber(symbolStyle.minzoom);
            minZoom = symbolStyle.minzoom;
        }
        if (symbolStyle?.maxzoom) {
            AssertService.isNumber(symbolStyle.maxzoom);
            maxZoom = symbolStyle.maxzoom;
        }

        return new FormGroup<GeometryFormGroup>({
            color: new FormControl<string>(this.symbologyEditService.pointColor(), { nonNullable: true }),
            label: this.getLabelFormGroupForGeometry(MapboxStyle.TypeEnum.SYMBOL),
            maxZoom: new FormControl<number | null>(maxZoom),
            minZoom: new FormControl<number | null>(minZoom),
            opacity: new FormControl<number | null>(opacity),
            size: new FormControl<number>(size),
            symbol: new FormControl<string>(symbol, { nonNullable: true }),
        });
    }

    private mergeStyles(styles: MapboxStyle[]): MergedGeometryStyle {
        return styles.reduce(
            (acc: MergedGeometryStyle, style: MapboxStyle) => {
                if (Object.keys(style.paint).length) {
                    if ('line-pattern' in style.paint && 'line-width' in style.paint) {
                        delete style.paint['line-width']; // you only need the line width, the symbol width is calculated from it
                    }
                    if ('fill-pattern' in style.paint && 'fill-opacity' in style.paint) {
                        acc.paint['fill-opacity-symbol'] = style.paint['fill-opacity'];
                    }
                    acc.paint = { ...acc.paint, ...style.paint };
                }

                if (style.layout && Object.keys(style.layout).length) {
                    acc.layout = { ...acc.layout, ...style.layout };
                }

                if (style.minzoom) {
                    acc.minZoom = style.minzoom;
                }

                if (style.maxzoom) {
                    acc.maxZoom = style.maxzoom;
                }

                return acc;
            },
            { layout: {}, maxZoom: null, minZoom: null, paint: {} },
        );
    }
}
